import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config";
import { extras } from "../i18n";
import { useLang } from "../i18n";
import { Bike, Camera, CheckCircle2, FileText, Loader2, Lock, ShieldCheck, Timer, UploadCloud, XCircle, Zap } from "lucide-react";
import SiempriaCredit from "../components/SiempriaCredit";

const DOC_FIELDS = [
  ["dni_front", "identity", "front"],
  ["dni_back", "identity", "back"],
  ["carnet_front", "license", "front"],
  ["carnet_back", "license", "back"],
];

function DocUpload({ id, title, side, file, onChange }) {
  const inputRef = useRef(null);
  const isImage = file && (file.type || "").startsWith("image/");
  return (
    <div className="border-2 border-topacio-ink rounded-xl bg-white overflow-hidden shadow-[3px_3px_0_0_#111]">
      <div className="px-3 py-2 border-b-2 border-topacio-ink bg-topacio-sand flex items-center justify-between gap-2">
        <span className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-ink">{title}</span>
        <span className="badge-brutal bg-brand text-topacio-ink !text-[9px]">{side}</span>
      </div>
      <button type="button" data-testid={`precheckin-upload-${id}`} onClick={() => inputRef.current?.click()}
        className="w-full aspect-[4/3] flex flex-col items-center justify-center gap-2 text-topacio-faded hover:bg-brand/20 transition-colors">
        {file ? (
          isImage ? (
            <img src={URL.createObjectURL(file)} alt={id} className="w-full h-full object-cover" />
          ) : (
            <><FileText className="w-8 h-8 text-topacio-green" /><span className="text-[10px] font-extrabold text-topacio-ink px-3 truncate max-w-full">{file.name}</span></>
          )
        ) : (
          <><Camera className="w-8 h-8" /><span className="text-[10px] font-extrabold uppercase tracking-wider px-3">{side}</span></>
        )}
      </button>
      <input ref={inputRef} data-testid={`precheckin-input-${id}`} type="file" accept="image/*,application/pdf" capture="environment" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f && f.size > 10 * 1024 * 1024) { toast.error("Archivo demasiado grande (máx. 10 MB)"); e.target.value = ""; return; } if (f) onChange(id, f); e.target.value = ""; }} />
    </div>
  );
}

export default function PrecheckinPage() {
  const { token } = useParams();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", dni: "", address: "",
    doc_type: "", nationality: "", sex: "", license_number: "", license_valid_until: "" });
  const [files, setFiles] = useState({});

  useEffect(() => {
    axios.get(`${API}/precheckin/${token}`)
      .then(({ data: d }) => {
        setData(d);
        const p = d.profile || {};
        setForm({ name: p.name || "", phone: p.phone || "", dni: p.dni || "", address: p.address || "",
          doc_type: p.doc_type || "", nationality: p.nationality || "", sex: p.sex || "",
          license_number: p.license_number || "", license_valid_until: p.license_valid_until || "" });
      })
      .catch(() => setError(true));
  }, [token]);

  const copy = extras[data?.booking?.lang || lang] || extras.es;
  const p = copy.precheckin;
  const ready = DOC_FIELDS.every(([id]) => files[id]);
  const already = data?.precheckin?.complete;

  const submit = async () => {
    if (!consent) {
      toast.error(p.consentRequired);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("falsity_consent", consent ? "true" : "false");
      DOC_FIELDS.forEach(([id]) => fd.append(id, files[id]));
      await axios.post(`${API}/precheckin/${token}`, fd);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const code = e.response?.data?.detail;
      if (["precheckin_not_found", "precheckin_expired", "booking_cancelled"].includes(code)) setError(true);
      else if (code === "consent_required") toast.error(p.consentRequired);
      else toast.error(code === "too_big" ? "Archivo demasiado grande (máx. 10 MB)" : code === "bad_type" ? "Solo imágenes o PDF" : code === "upload_failed" ? "No se pudieron subir los documentos. Revisa que sean imágenes o PDF e inténtalo de nuevo." : code === "payment_unavailable" || code === "payment_error" ? "El pago con tarjeta no está disponible ahora mismo. Inténtalo más tarde o contáctanos." : "No se pudo enviar. Inténtalo de nuevo.");
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-topacio-sand flex items-center justify-center px-6" data-testid="precheckin-error">
        <div className="card-brutal max-w-md w-full p-8 text-center">
          <XCircle className="w-12 h-12 text-topacio-red mx-auto" />
          <h1 className="font-display font-black text-xl text-topacio-ink mt-4">{extras[lang]?.precheckin.invalidTitle || extras.es.precheckin.invalidTitle}</h1>
          <p className="text-sm text-topacio-faded font-semibold mt-2">{extras[lang]?.precheckin.invalidSub || extras.es.precheckin.invalidSub}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-topacio-sand flex items-center justify-center" data-testid="precheckin-loading">
        <p className="flex items-center gap-2 font-bold text-topacio-faded"><Loader2 className="w-5 h-5 animate-spin" /> {extras[lang]?.precheckin.loading || extras.es.precheckin.loading}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-topacio-sand" data-testid="precheckin-page">
      <header className="border-b-2 border-topacio-ink bg-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <img src="/logo-topacio.jpg" alt="Topacio" className="h-10 w-auto rounded border-2 border-topacio-ink" />
          <div>
            <p className="font-display font-black text-topacio-ink leading-none">TOPACIO</p>
            <p className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-topacio-faded mt-1">Precheck-in</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {done || already ? (
          <section className="card-brutal p-8 text-center" data-testid="precheckin-success">
            <span className="inline-flex w-20 h-20 rounded-full bg-topacio-green border-4 border-topacio-ink items-center justify-center shadow-[4px_4px_0_0_#111]">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </span>
            <h1 className="font-display font-black text-2xl text-topacio-ink mt-6">{already && !done ? p.alreadyTitle : p.successTitle}</h1>
            <p className="text-sm text-topacio-faded font-semibold mt-3 max-w-xl mx-auto">{already && !done ? p.alreadySub : p.successSub}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8">
              {[p.benefit1, p.benefit2, p.benefit3].map((b, i) => (
                <div key={b} className="border-2 border-topacio-ink rounded-lg bg-white px-3 py-4 text-xs font-extrabold text-topacio-ink flex items-center justify-center gap-2">
                  {i === 0 ? <ShieldCheck className="w-4 h-4 text-topacio-green" /> : i === 1 ? <Timer className="w-4 h-4 text-topacio-red" /> : <Zap className="w-4 h-4 text-brand" />} {b}
                </div>
              ))}
            </div>
          </section>
        ) : (
          <>
            <section className="card-brutal p-6 sm:p-8 mb-6 relative overflow-hidden">
              <div className="absolute -right-8 -top-8 w-36 h-36 rounded-full bg-brand border-4 border-topacio-ink opacity-30" />
              <p className="eyebrow mb-3">Renta Moto Topacio</p>
              <h1 className="font-display font-black text-3xl text-topacio-ink leading-tight max-w-xl">{p.title}</h1>
              <p className="text-sm text-topacio-faded font-semibold mt-3 max-w-xl">{p.sub}</p>
              <div className="flex flex-wrap gap-2 mt-5">
                <span className="badge-brutal bg-topacio-ink text-white"><ShieldCheck className="w-3 h-3" /> {p.benefit1}</span>
                <span className="badge-brutal bg-brand text-topacio-ink"><Timer className="w-3 h-3" /> {p.benefit2}</span>
                <span className="badge-brutal bg-topacio-green text-white"><Zap className="w-3 h-3" /> {p.benefit3}</span>
              </div>
            </section>

            <section className="card-brutal p-5 mb-6" data-testid="precheckin-summary">
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-topacio-faded mb-3">{p.summary}</p>
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 bg-brand border-2 border-topacio-ink rounded-lg flex items-center justify-center shrink-0"><Bike className="w-6 h-6 text-topacio-ink" /></span>
                <div>
                  <p className="font-display font-black text-topacio-ink">{data.booking.bike_name}</p>
                  <p className="text-xs font-bold text-topacio-faded mt-1">{data.booking.ref} · {data.booking.start_date} → {data.booking.end_date}</p>
                  <p className="text-xs font-semibold text-topacio-faded">{data.booking.name} · {data.booking.email}</p>
                </div>
              </div>
            </section>

            <section className="card-brutal p-5 sm:p-6 mb-6">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h2 className="font-display font-bold text-topacio-ink flex items-center gap-2"><UploadCloud className="w-5 h-5 text-topacio-red" /> {p.docsTitle}</h2>
                <span data-testid="precheckin-progress" className="badge-brutal bg-topacio-ink text-white">{DOC_FIELDS.filter(([id]) => files[id]).length}/4 {p.progress}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {DOC_FIELDS.map(([id, doc, side]) => (
                  <DocUpload key={id} id={id} title={p[doc]} side={p[side]} file={files[id]}
                    onChange={(key, file) => setFiles({ ...files, [key]: file })} />
                ))}
              </div>
              <p className="text-[10px] text-topacio-faded font-semibold mt-3">{p.once} JPG, PNG, WebP o PDF · máx. 10 MB por archivo.</p>
            </section>

            <section className="card-brutal p-5 sm:p-6 mb-6">
              <h2 className="font-display font-bold text-topacio-ink mb-4">{p.dataTitle}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid="precheckin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={p.name} className="input-brutal" />
                <input data-testid="precheckin-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={p.phone} className="input-brutal" />
                <input data-testid="precheckin-dni" value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} placeholder={p.dni} className="input-brutal" />
                <input data-testid="precheckin-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={p.address} className="input-brutal" />
              </div>
              <div className="mt-5 border-t-2 border-dashed border-zinc-200 pt-4">
                <p className="text-[10px] uppercase tracking-wider font-extrabold text-topacio-faded mb-3">{p.sesTitle}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select data-testid="precheckin-doc-type" value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="input-brutal">
                    <option value="">{p.docType}</option>
                    <option value="NIF">DNI / NIF</option>
                    <option value="NIE">NIE</option>
                    <option value="PAS">{p.passport}</option>
                    <option value="OTRO">{p.other}</option>
                  </select>
                  <input data-testid="precheckin-nationality" value={form.nationality} maxLength={3} onChange={(e) => setForm({ ...form, nationality: e.target.value.toUpperCase() })} placeholder={p.nationality} className="input-brutal" />
                  <select data-testid="precheckin-sex" value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })} className="input-brutal">
                    <option value="">{p.sex}</option>
                    <option value="H">{p.sexM}</option>
                    <option value="M">{p.sexF}</option>
                  </select>
                  <input data-testid="precheckin-license-number" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} placeholder={p.licenseNumber} className="input-brutal" />
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-extrabold text-topacio-faded">{p.licenseValid}</label>
                    <input data-testid="precheckin-license-valid" type="date" value={form.license_valid_until} onChange={(e) => setForm({ ...form, license_valid_until: e.target.value })} className="input-brutal mt-1" />
                  </div>
                </div>
              </div>
            </section>

            <label data-testid="precheckin-consent" className="flex items-start gap-3 card-brutal !shadow-none p-4 cursor-pointer text-left">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-5 h-5 mt-0.5 accent-[#FFCC00] shrink-0" />
              <span className="text-xs font-semibold text-topacio-ink leading-relaxed">{p.consent}</span>
            </label>

            <button data-testid="precheckin-submit" onClick={submit} disabled={!ready || busy}
              className="btn-primary w-full !py-4 !text-sm disabled:opacity-50">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              {busy ? p.sending : p.submit}
            </button>
            {!ready && <p data-testid="precheckin-required" className="text-center text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded mt-3">{p.required}</p>}
            <p className="flex items-center justify-center gap-2 text-[10px] text-zinc-400 font-bold mt-4"><Lock className="w-3.5 h-3.5" /> {p.secure}</p>
          </>
        )}
        <div className="mt-10 text-center"><SiempriaCredit /></div>
      </main>
    </div>
  );
}
